import { useCallback, useEffect, useRef, useState } from 'react';

import { ConfirmationDialog } from '@/components/confirmation-dialog';
import { useWorkflowStep } from '@/context';
import { MeasurementType } from '@/visualization/domain/sizing';
import {
    CustomMeasurementButton,
    CustomMeasurementDraftRow,
    CustomMeasurementReassignRow,
    DiameterGroup,
    KeypointBar,
    MeasurementRow,
    MeasurementSection,
    CarmViewRow,
} from '@/workflows/vascular/common/components/measurements';
import { useCarmViews } from '@/workflows/vascular/common/hooks/use-carm-views';
import {
    useSizingState,
    type DraftKind,
    type DraftSlot,
} from '@/workflows/vascular/common/hooks/use-sizing-state';

/**
 * Maps measurement categories to colors
 * Selected colors are close to the ones used in sizing sheet but can differ a bit
 * to be clearly readable, even on dark theme
 */
const MEASUREMENT_CATEGORY_COLORS: Record<MeasurementType, string> = {
    diameter: '#009FE3',
    length: '#00A993',
    angle: '#4C3087',
    derived: '#d36c15',
    'offset-diameter': '#009FE3',
    'min-max-diameter': '#009FE3',
};

/**
 * Measurements panel content — extracted from the original EvarSidebar.
 * Renders keypoint bar, diameter groups, lengths, angles, derived, and C-arm views.
 */
export function EvarMeasurementsPanel() {
    const sizing = useSizingState();
    const carmViews = useCarmViews(true);
    const { activeStepId } = useWorkflowStep();
    /** Action held back by the discard dialog, run once the user confirms. */
    const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

    // Use refs so the deselect effect only fires on step transitions, not on every sizing/carmViews update
    const sizingRef = useRef(sizing);
    sizingRef.current = sizing;
    const carmViewsRef = useRef(carmViews);
    carmViewsRef.current = carmViews;

    // Deselect measurement, cancel any draft, and deselect C-arm view when leaving the measurements step
    useEffect(() => {
        if (activeStepId !== 'measurements') {
            sizingRef.current?.deselectMeasurement();
            sizingRef.current?.cancelDraft();
            carmViewsRef.current?.deselectView();
        }
    }, [activeStepId]);

    /**
     * Run an action that would drop the active C-arm draft, asking first when
     * the draft holds unsaved angle changes. The action itself cancels the
     * draft (through deselectView/beginEdit/deleteView), so confirming needs
     * nothing more.
     *
     * @param dropsDraft false for an action that leaves the draft alone — no
     * confirmation is owed when nothing would be lost.
     */
    const guardCarmDraft = useCallback(
        (action: () => void, dropsDraft = true) => {
            if (dropsDraft && carmViews?.isEditDirty()) {
                setPendingAction(() => action);
            } else {
                action();
            }
        },
        [carmViews]
    );

    const handleDiscardConfirmed = useCallback(() => {
        const action = pendingAction;
        setPendingAction(null);
        action?.();
    }, [pendingAction]);

    /** Move keypoint to the current slice position. */
    const handleSetKeypoint = useCallback(
        (keypointId: string) => {
            sizing?.moveKeypointToCurrentLocation(keypointId);
        },
        [sizing]
    );

    /** Select a measurement, navigate to its position, and deselect any C-arm view. */
    const handleSelect = useCallback(
        (id: string) => {
            guardCarmDraft(() => {
                carmViews?.deselectView();
                sizing?.selectMeasurement(id);
                sizing?.navigateToMeasurement(id);
            });
        },
        [sizing, carmViews, guardCarmDraft]
    );

    /**
     * Toggle C-arm edit mode for a view (deselects any measurement and cancels any draft).
     * Clicking the row being edited quits the edit — the draft is cancelled
     * and the pre-selection camera restored.
     */
    const handleSelectCarmView = useCallback(
        (index: number) => {
            guardCarmDraft(() => {
                sizing?.deselectMeasurement();
                sizing?.cancelDraft();
                if (carmViews?.editTarget === index) {
                    carmViews.deselectView();
                } else {
                    carmViews?.beginEdit(index);
                }
            });
        },
        [sizing, carmViews, guardCarmDraft]
    );

    /** Enter C-arm edit mode to create a new view (deselects any measurement and cancels any draft). */
    const handleAddCarmView = useCallback(() => {
        guardCarmDraft(() => {
            sizing?.deselectMeasurement();
            sizing?.cancelDraft();
            carmViews?.beginEdit('new');
        });
    }, [sizing, carmViews, guardCarmDraft]);

    /** Delete a custom C-arm view — deleting shifts indices, so it drops any draft. */
    const handleDeleteCarmView = useCallback(
        (index: number) => guardCarmDraft(() => carmViews?.deleteView(index)),
        [carmViews, guardCarmDraft]
    );

    /**
     * Reset an edited pre-computed C-arm view to its original angles. Only the
     * draft on that same view goes with it (see CarmViewService.resetView), so
     * a draft on another view is left alone and needs no confirmation.
     */
    const handleResetCarmView = useCallback(
        (index: number) => guardCarmDraft(() => carmViews?.resetView(index), carmViews?.editTarget === index),
        [carmViews, guardCarmDraft]
    );

    /** Relocate a diameter measurement to the current slice position. */
    const handleLocateDiameter = useCallback(
        (measurementId: string) => {
            sizing?.relocateMeasurementToCurrentLocation(measurementId);
        },
        [sizing]
    );

    const handleAddOffset = useCallback(
        (keypointId: string, offsetMm: number) => {
            sizing?.addOffsetDiameter(keypointId, offsetMm);
        },
        [sizing]
    );

    /** Add a custom diameter at the current slice position, using edited diameter if available. */
    const handleAddCustomDiameter = useCallback(() => {
        sizing?.addCustomDiameterAtCurrentLocation();
    }, [sizing]);

    /** Rename a custom measurement. */
    const handleRenameCustom = useCallback(
        (id: string, newName: string) => {
            sizing?.renameCustomMeasurement(id, newName);
        },
        [sizing]
    );

    /** Begin a custom length/angle draft (deselect any C-arm view first). */
    const handleBeginDraft = useCallback(
        (kind: DraftKind) => {
            guardCarmDraft(() => {
                carmViews?.deselectView();
                sizing?.beginCustomDraft(kind);
            });
        },
        [sizing, carmViews, guardCarmDraft]
    );

    const handleSetDraftPoint = useCallback(
        (slot: DraftSlot) => {
            sizing?.setDraftPointToCurrentLocation(slot);
        },
        [sizing]
    );

    const handleCancelDraft = useCallback(() => {
        sizing?.cancelDraft();
    }, [sizing]);

    /** Re-assign one point of a committed custom length/angle to the current location. */
    const handleReassign = useCallback(
        (id: string, slot: DraftSlot) => {
            sizing?.reassignCustomMeasurementPoint(id, slot);
        },
        [sizing]
    );

    if (!sizing) return null;

    const {
        measurements,
        diameterGroups,
        minMaxDiameters,
        customDiameters,
        customLengths,
        customAngles,
        selectedMeasurementId,
        draft,
        isDraftComplete,
        canCommitDraft,
    } = sizing;

    // Custom lengths/angles carry LENGTH/ANGLE categories, so they also surface in
    // measurements.lengths/.angles — keep the generic sections template-only.
    const templateLengths = measurements.lengths.filter((m) => m.context.type === 'length');
    const templateAngles = measurements.angles.filter((m) => m.context.type === 'angle');

    return (
        <div className="flex h-full flex-col overflow-hidden">
            <KeypointBar keypoints={sizing.editableKeypoints} onSetKeypoint={handleSetKeypoint} />

            <div className="flex-1 overflow-y-auto">
                {/* Diameters */}
                <MeasurementSection title="Diameters" groupColor={MEASUREMENT_CATEGORY_COLORS.diameter}>
                    {diameterGroups.map((group) => (
                        <DiameterGroup
                            key={group.keypointId}
                            group={group}
                            selectedMeasurementId={selectedMeasurementId}
                            onSelect={handleSelect}
                            onAddOffset={handleAddOffset}
                            onRemoveOffset={sizing.removeOffsetDiameter}
                        />
                    ))}
                    {minMaxDiameters.map((m) => (
                        <MeasurementRow
                            key={m.id}
                            id={m.id}
                            name={m.shortLabel}
                            description={m.name}
                            value={m.value}
                            unit={m.unit}
                            isSelected={selectedMeasurementId === m.id}
                            onSelect={handleSelect}
                            onLocate={() => handleLocateDiameter(m.id)}
                        />
                    ))}

                    {customDiameters.map((m) => (
                        <MeasurementRow
                            key={m.id}
                            id={m.id}
                            name={m.shortLabel}
                            value={m.value}
                            unit={m.unit}
                            isSelected={selectedMeasurementId === m.id}
                            onSelect={handleSelect}
                            onLocate={() => handleLocateDiameter(m.id)}
                            onDelete={() => sizing.removeCustomMeasurement(m.id)}
                            onRename={(newName) => handleRenameCustom(m.id, newName)}
                        />
                    ))}

                    <CustomMeasurementButton label="Add Diameter" onAddCustom={handleAddCustomDiameter} />
                </MeasurementSection>

                {/* Lengths (template) */}
                <MeasurementSection title="Lengths" groupColor={MEASUREMENT_CATEGORY_COLORS.length}>
                    {templateLengths.map((m) => (
                        <MeasurementRow
                            key={m.id}
                            id={m.id}
                            name={m.shortLabel}
                            description={m.name}
                            value={m.value}
                            unit={m.unit}
                            isSelected={selectedMeasurementId === m.id}
                            onSelect={handleSelect}
                        />
                    ))}

                    {customLengths.map((m) => (
                        <div key={m.id}>
                            <MeasurementRow
                                id={m.id}
                                name={m.shortLabel}
                                value={m.value}
                                unit={m.unit}
                                isSelected={selectedMeasurementId === m.id}
                                onSelect={handleSelect}
                                onDelete={() => sizing.removeCustomMeasurement(m.id)}
                                onRename={(newName) => handleRenameCustom(m.id, newName)}
                            />
                            {selectedMeasurementId === m.id && (
                                <CustomMeasurementReassignRow
                                    kind="length"
                                    measurementId={m.id}
                                    onReassign={handleReassign}
                                />
                            )}
                        </div>
                    ))}
                    <CustomMeasurementDraftRow
                        kind="length"
                        draft={draft}
                        isDraftComplete={isDraftComplete}
                        canCommit={canCommitDraft}
                        onBegin={handleBeginDraft}
                        onSetPoint={handleSetDraftPoint}
                        onCancel={handleCancelDraft}
                    />
                </MeasurementSection>

                {/* Angles (template) */}
                <MeasurementSection title="Angles" groupColor={MEASUREMENT_CATEGORY_COLORS.angle}>
                    {templateAngles.map((m) => (
                        <MeasurementRow
                            key={m.id}
                            id={m.id}
                            name={m.shortLabel}
                            description={m.name}
                            value={m.value}
                            unit={m.unit}
                            isSelected={selectedMeasurementId === m.id}
                            onSelect={handleSelect}
                        />
                    ))}

                    {customAngles.map((m) => (
                        <div key={m.id}>
                            <MeasurementRow
                                id={m.id}
                                name={m.shortLabel}
                                value={m.value}
                                unit={m.unit}
                                isSelected={selectedMeasurementId === m.id}
                                onSelect={handleSelect}
                                onDelete={() => sizing.removeCustomMeasurement(m.id)}
                                onRename={(newName) => handleRenameCustom(m.id, newName)}
                            />
                            {selectedMeasurementId === m.id && (
                                <CustomMeasurementReassignRow
                                    kind="angle"
                                    measurementId={m.id}
                                    onReassign={handleReassign}
                                />
                            )}
                        </div>
                    ))}
                    <CustomMeasurementDraftRow
                        kind="angle"
                        draft={draft}
                        isDraftComplete={isDraftComplete}
                        canCommit={canCommitDraft}
                        onBegin={handleBeginDraft}
                        onSetPoint={handleSetDraftPoint}
                        onCancel={handleCancelDraft}
                    />
                </MeasurementSection>

                {/* Derived */}
                {measurements.derived.length > 0 && (
                    <MeasurementSection title="Derived" groupColor={MEASUREMENT_CATEGORY_COLORS.derived}>
                        {measurements.derived.map((m) => (
                            <MeasurementRow
                                key={m.id}
                                id={m.id}
                                name={m.shortLabel}
                                description={m.name}
                                value={m.value}
                                unit={m.unit}
                                isSelected={selectedMeasurementId === m.id}
                                onSelect={handleSelect}
                            />
                        ))}
                    </MeasurementSection>
                )}

                {/* C-arm Views */}
                {carmViews && (
                    <MeasurementSection title="C-arm Views">
                        {carmViews.views.map((view, index) => (
                            <CarmViewRow
                                key={view.id}
                                view={view}
                                index={index}
                                isSelected={
                                    carmViews.selectedIndex === index || carmViews.editTarget === index
                                }
                                isEdited={carmViews.isEdited(index)}
                                isCustom={!carmViews.isPrecomputed(index)}
                                onSelect={handleSelectCarmView}
                                onRename={carmViews.renameView}
                                onDelete={!carmViews.isPrecomputed(index) ? handleDeleteCarmView : undefined}
                                onReset={
                                    carmViews.isPrecomputed(index) && carmViews.isEdited(index)
                                        ? handleResetCarmView
                                        : undefined
                                }
                            />
                        ))}

                        <CustomMeasurementButton label="Add CArm preset" onAddCustom={handleAddCarmView} />
                    </MeasurementSection>
                )}
            </div>

            <ConfirmationDialog
                open={pendingAction !== null}
                onOpenChange={(open) => !open && setPendingAction(null)}
                title="Discard C-arm changes?"
                description="The adjustments made to this C-arm preset have not been applied. Continuing discards them."
                confirmLabel="Discard changes"
                cancelLabel="Keep editing"
                variant="destructive"
                onConfirm={handleDiscardConfirmed}
            />
        </div>
    );
}
